import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FileText, Search, Filter, RefreshCw, Eye, Edit, Trash2, Download, Plus, Calendar, BarChart3, CheckCircle2, XCircle, Clock, AlertCircle, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Copy } from 'lucide-react';
import { reportsService } from '../services/reportsService';
import { authService } from '../services/authService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Reports List Page
 * Displays all reports with filtering, sorting, and pagination
 */
const Reports = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reports, setReports] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    per_page: 20,
    total_pages: 0,
  });
  const [filters, setFilters] = useState({
    status: 'all',
    account_id: 'all',
    search: '',
  });
  const [deletingId, setDeletingId] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);

  // Fetch reports
  const fetchReports = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page,
        per_page: pagination.per_page,
      };
      
      if (filters.status && filters.status !== 'all') {
        params.status = filters.status;
      }
      
      if (filters.account_id && filters.account_id !== 'all') {
        params.account_id = parseInt(filters.account_id);
      }
      
      const response = await reportsService.list(params);
      
      // Filter by search term if provided
      let reportsData = response.data?.reports || [];
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        reportsData = reportsData.filter(report => 
          report.report_name?.toLowerCase().includes(searchLower)
        );
      }
      
      setReports(reportsData);
      setPagination(response.data?.pagination || pagination);
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to load reports';
      setError(errorMessage);
      console.error('Reports fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await authService.getAccounts();
        setAccounts(response.data?.accounts || []);
      } catch (err) {
        console.error('Error fetching accounts:', err);
      }
    };
    fetchAccounts();
  }, []);

  // Track if component has mounted to avoid double-fetching
  const hasMountedRef = useRef(false);
  const prevPathRef = useRef(null);

  // Fetch reports on mount and when filters change
  useEffect(() => {
    fetchReports(1);
    hasMountedRef.current = true;
    prevPathRef.current = location.pathname;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.account_id]);

  // Refetch when navigating to this page from another route (e.g., after creating a report)
  useEffect(() => {
    // Only refetch if we've mounted and navigated to /reports from a different route
    if (hasMountedRef.current && location.pathname === '/reports' && prevPathRef.current !== '/reports') {
      fetchReports(pagination.page);
    }
    prevPathRef.current = location.pathname;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (filters.search !== undefined) {
        fetchReports(pagination.page);
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    fetchReports(newPage);
  };

  const handleDelete = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) {
      return;
    }

    try {
      setDeletingId(reportId);
      await reportsService.delete(reportId);
      // Refresh the list
      fetchReports(pagination.page);
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to delete report';
      alert(errorMessage);
      console.error('Delete error:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleExport = async (reportId, format) => {
    try {
      let exportFn;
      switch (format) {
        case 'csv':
          exportFn = reportsService.exportCsv;
          break;
        case 'json':
          exportFn = reportsService.exportJson;
          break;
        case 'pdf':
          exportFn = reportsService.exportPdf;
          break;
        default:
          return;
      }

      await exportFn(reportId);
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to export report';
      alert(errorMessage);
      console.error('Export error:', err);
    }
  };

  const handleDuplicate = async (reportId) => {
    try {
      setDuplicatingId(reportId);
      const response = await reportsService.duplicate(reportId);
      
      // Refresh the list
      await fetchReports(pagination.page);
      
      // Navigate to the new duplicate
      if (response.data?.report_id) {
        navigate(`/reports/${response.data.report_id}`);
      }
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to duplicate report';
      alert(errorMessage);
      console.error('Duplicate error:', err);
    } finally {
      setDuplicatingId(null);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { variant: 'secondary', icon: FileText, label: 'Draft' },
      ready: { variant: 'default', icon: CheckCircle2, label: 'Ready' },
      generating: { variant: 'secondary', icon: Clock, label: 'Generating' },
      failed: { variant: 'destructive', icon: XCircle, label: 'Failed' },
    };

    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  // Table columns
  const columns = useMemo(() => [
    {
      accessorKey: 'report_name',
      header: 'Report Name',
      cell: ({ row }) => {
        const report = row.original;
        return (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <div className="font-medium">{report.report_name}</div>
              <div className="text-sm text-muted-foreground">
                {report.metrics?.length > 0 && (
                  <span>{report.metrics.length} metric{report.metrics.length > 1 ? 's' : ''}</span>
                )}
                {report.breakdowns?.length > 0 && (
                  <span className="ml-2">{report.breakdowns.length} breakdown{report.breakdowns.length > 1 ? 's' : ''}</span>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const status = getValue();
        return getStatusBadge(status);
      },
    },
    {
      accessorKey: 'format',
      header: 'Format',
      cell: ({ getValue }) => {
        const format = getValue();
        const formatLabels = {
          table: 'Table',
          chart: 'Chart',
          both: 'Table & Chart',
        };
        return (
          <Badge variant="outline">
            {formatLabels[format] || format}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'start_date',
      header: 'Date Range',
      cell: ({ row }) => {
        const report = row.original;
        if (!report.start_date || !report.end_date) return '-';
        const start = new Date(report.start_date).toLocaleDateString();
        const end = new Date(report.end_date).toLocaleDateString();
        return (
          <div className="text-sm">
            <div>{start}</div>
            <div className="text-muted-foreground">to {end}</div>
          </div>
        );
      },
    },
    {
      accessorKey: 'created_at',
      header: 'Created',
      cell: ({ getValue }) => {
        const date = getValue();
        if (!date) return '-';
        return (
          <div className="text-sm">
            {new Date(date).toLocaleDateString()}
            <div className="text-muted-foreground">
              {new Date(date).toLocaleTimeString()}
            </div>
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const report = row.original;
        const isDeleting = deletingId === report.id;
        const isDuplicating = duplicatingId === report.id;
        
        return (
          <div className="flex items-center gap-2">
            <Button
              asChild
              variant="outline"
              size="sm"
            >
              <Link to={`/reports/${report.id}`}>
                <Eye className="w-4 h-4 mr-1" />
                View
              </Link>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={isDeleting || isDuplicating}>
                  <Download className="w-4 h-4 mr-1" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport(report.id, 'csv')}>
                  <FileText className="w-4 h-4 mr-2" />
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport(report.id, 'json')}>
                  <FileText className="w-4 h-4 mr-2" />
                  Export as JSON
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport(report.id, 'pdf')}>
                  <FileText className="w-4 h-4 mr-2" />
                  Export as PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDuplicate(report.id)}
              disabled={isDeleting || isDuplicating}
              title="Duplicate report"
            >
              <Copy className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/reports/${report.id}/edit`)}
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDelete(report.id)}
              disabled={isDeleting || isDuplicating}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        );
      },
    },
  ], [deletingId, duplicatingId, navigate]);

  const table = useReactTable({
    data: reports,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount: pagination.total_pages,
    state: {
      pagination: {
        pageIndex: pagination.page - 1,
        pageSize: pagination.per_page,
      },
    },
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
      <div className="mb-2 sm:mb-3 lg:mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold mb-0.5">Reports</h1>
          <p className="text-muted-foreground text-xs">Manage and view your custom reports</p>
        </div>
        <Button asChild className="w-full sm:w-auto">
          <Link to="/reports/builder">
            <Plus className="w-4 h-4 mr-2" />
            Create Report
          </Link>
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-2 sm:mb-3 lg:mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <div>
              <label className="block text-sm font-medium mb-1.5">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search reports..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Status</label>
              <Select
                value={filters.status}
                onValueChange={(value) => handleFilterChange('status', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="ready">Ready</SelectItem>
                  <SelectItem value="generating">Generating</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Account</label>
              <Select
                value={filters.account_id}
                onValueChange={(value) => handleFilterChange('account_id', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All accounts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All accounts</SelectItem>
                  {accounts.map(account => (
                    <SelectItem key={account.id} value={account.id.toString()}>
                      {account.account_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => fetchReports(pagination.page)}
                className="w-full"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error message */}
      {error && (
        <Card className="mb-6 border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reports table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Loading reports...</div>
          ) : reports.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-lg font-medium mb-2">No reports found</p>
              <p className="text-muted-foreground mb-4">Create your first report to get started</p>
              <Button asChild>
                <Link to="/reports/builder">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Report
                </Link>
              </Button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                      <tr key={headerGroup.id} className="border-b">
                        {headerGroup.headers.map(header => (
                          <th
                            key={header.id}
                            className="h-12 px-2 sm:px-4 text-left align-middle font-medium text-muted-foreground text-xs sm:text-sm"
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map(row => (
                      <tr
                        key={row.id}
                        className="border-b transition-colors hover:bg-muted/50"
                      >
                        {row.getVisibleCells().map(cell => (
                          <td
                            key={cell.id}
                            className="p-2 sm:p-4 align-middle text-sm"
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border-t gap-4">
                  <div className="text-sm text-muted-foreground text-center sm:text-left">
                    Showing {((pagination.page - 1) * pagination.per_page) + 1} to{' '}
                    {Math.min(pagination.page * pagination.per_page, pagination.total)} of{' '}
                    {pagination.total} reports
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>
                    <div className="text-sm">
                      Page {pagination.page} of {pagination.total_pages}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page >= pagination.total_pages}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Reports;
